import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import { computed, ref } from 'vue'

export const UseLocalizationOrganizationFragment = graphql(`
    fragment UseLocalizationOrganizationFragment on Organization {
        locale
        currencyFormat
        startOfWeek
    }
`)

const organizationFragment =
    ref<FragmentType<typeof UseLocalizationOrganizationFragment>>()

export function provideLocalizationOrganizationFragment(
    organization: FragmentType<typeof UseLocalizationOrganizationFragment>
) {
    organizationFragment.value = organization
}

const localeData = computed(() => {
    const organizationData = getFragmentData(
        UseLocalizationOrganizationFragment,
        organizationFragment.value
    )

    if (organizationData === undefined) {
        return {
            currencyFormat: 'USD',
            locale: 'en-US',
            startOfWeek: 0
        }
    }

    return {
        currencyFormat: organizationData.currencyFormat,
        locale: organizationData.locale,
        startOfWeek: organizationData.startOfWeek
    }
})

const currencyFormatter = computed(() => {
    return new Intl.NumberFormat(localeData.value.locale, {
        style: 'currency',
        currency: localeData.value.currencyFormat
    })
})

const dateTimeFormatter = computed(() => {
    return new Intl.DateTimeFormat(localeData.value.locale, {
        dateStyle: 'long',
        timeStyle: 'short'
    })
})

const dateFormatter = computed(() => {
    return new Intl.DateTimeFormat(localeData.value.locale, {
        dateStyle: 'long'
    })
})

export enum DateOrder {
    MonthDayYear,
    DayMonthYear,
    YearMonthDay
}

const datePartOrder = computed(() => {
    const parts = new Intl.DateTimeFormat(
        localeData.value.locale
    ).formatToParts()
    const filteredParts = parts.filter(part =>
        ['year', 'month', 'day'].includes(part.type)
    )

    // Don't have all three, return month day year
    if (filteredParts.length === 3) {
        // If year is first return that
        if (filteredParts[0].type === 'year') {
            return DateOrder.YearMonthDay
        }

        // If day is first
        if (filteredParts[0].type === 'day') {
            return DateOrder.DayMonthYear
        }
    }

    // Fall back to US format
    return DateOrder.MonthDayYear
})

const startOfWeek = computed(() => {
    return localeData.value.startOfWeek
})

export function useLocalization() {
    return {
        currencyFormatter,
        dateTimeFormatter,
        dateFormatter,
        datePartOrder,
        startOfWeek
    }
}
